import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactClassTypesEnum } from '../../../../Enums';
import {
  GetHoldingCompanyNames,
  GetLookupItemsByLookupTypeName,
  lookupItemsGetId,
} from '../../../../Services';
import {
  AutocompleteComponent,
  Inputs,
  SelectComponet,
  PhonesComponent,
} from '../../../../Components';
import { showError } from '../../../../Helper';
import { useDebouncedAction } from '../../../../Hooks/DebouncedAction';

export const VendorsFiltersComponent = ({
  translationPath,
  parentTranslationPath,
  changeContactClass,
  vendorContactClasses,
  vendorContactClassesFilter,
  selectedValuesInFilters,
  setSelectedValuesInFilters,
  filterValues,
  setFilterValues,
  isAdvanceSearchCleared,
}) => {
  const { t } = useTranslation(parentTranslationPath);
  const [isLoading, setIsLoading] = useState({
    agreementStatus: false,
    vendorCategory: false,
    country: false,
    city: false,
    vendorClass: false,
    companyNames: false,
  });

  const [allItems, setAllItems] = useState({
    agreementStatus: [],
    vendorCategory: [],
    country: [],
    city: [],
    vendorClass: [],
    companyNames: [],
  });

  const [helperText, setHelperText] = useState({
    mobile: '',
    name: '',
  });

  const searchTimer = useRef(null);

  const getIsContactClassChecked = (item) =>
    vendorContactClassesFilter &&
    vendorContactClassesFilter.findIndex(
      (element) => element === item.value
    ) !== -1;

  const getLookupItemsByName = async (lookUpName, key) => {
    setIsLoading((item) => ({ ...item, [key]: true }));
    const result = await GetLookupItemsByLookupTypeName({
      lookUpName,
      pageSize: 1000,
      pageIndex: 1,
    });
    if (!(result && result.status && result.status !== 200)) {
      setAllItems((item) => ({
        ...item,
        [key]: (result && result.result) || [],
      }));
    } else setAllItems((item) => ({ ...item, [key]: [] }));
    setIsLoading((loading) => ({ ...loading, [key]: false }));
  };

  const lookupItemsGetIdAPI = async (key, key2, lookupTypeId) => {
    setIsLoading((item) => ({ ...item, [key]: true }));
    const result = await lookupItemsGetId({
      lookupTypeId: lookupTypeId,
    });
    if (!(result && result.status && result.status !== 200)) {
      setAllItems((item) => ({ ...item, [key2]: result || [] }));
    } else setAllItems((item) => ({ ...item, [key2]: [] }));
    setIsLoading((item) => ({ ...item, [key]: false }));
  };

  const getCompanyNames = useDebouncedAction(async (value) => {
    setIsLoading((item) => ({ ...item, companyNames: true }));
    try {
      const res = await GetHoldingCompanyNames({
        search: value,
        pageIndex: 1,
        pageSize: 100,
      });
      setAllItems((item) => ({ ...item, companyNames: res.result }));
    } catch {
      setAllItems((item) => ({ ...item, companyNames: [] }));
      showError('get company names error');
    } finally {
      setIsLoading((item) => ({ ...item, companyNames: false }));
    }
  });

  useEffect(() => {
    if (isAdvanceSearchCleared) {
      setAllItems({
        agreementStatus: [],
        vendorCategory: [],
        country: [],
        city: [],
        vendorClass: [],
        companyNames: [],
      });
      setHelperText({
        agreementStatus: false,
        vendorCategory: false,
        country: false,
        city: false,
        vendorClass: false,
      });
    }
  }, [isAdvanceSearchCleared]);

  useEffect(() => {
    if (allItems && allItems.vendorClass && allItems.vendorClass.length === 0)
      getLookupItemsByName('VendorClass', 'vendorClass');
  }, [allItems]);

  useEffect(() => {
    getCompanyNames();
  }, []);

  return (
    <div className='vendor-filters-wrapper'>
      <Inputs
        idRef='nameRef'
        fieldClasses='fields-content'
        wrapperClasses='w-25 m-0'
        inputPlaceholder={'vendor-name'}
        value={filterValues.name}
        onInputChanged={(e) => {
          let { value } = e?.target;
          setFilterValues((item) => ({
            ...item,
            name: value,
          }));
        }}
        onKeyUp={(e) => {
          let { value } = e.target;
          if (searchTimer.current) clearTimeout(searchTimer.current);
          searchTimer.current = setTimeout(() => {
            setSelectedValuesInFilters((item) => ({
              ...item,
              'vendor_name.name': value,
            }));
          }, 700);
        }}
        onKeyDown={() => {
          if (searchTimer) clearTimeout(searchTimer.current);
        }}
        parentTranslationPath={parentTranslationPath}
        translationPath={translationPath}
      />
      <PhonesComponent
        idRef={`mobileRef`}
        value={filterValues?.mobile || ''}
        helperText={helperText?.mobile !== '' ? helperText?.mobile : ''}
        error={helperText?.mobile !== ''}
        inputPlaceholder={'enter-mobile-number'}
        wrapperClasses='w-25'
        inputClasses='phone-fields-content'
        onInputChanged={(value) => {
          let newValue = value;
          newValue = newValue.replaceAll(' ', '');

          if (
            (newValue !== '' && newValue.length < 7) ||
            (newValue !== '' && newValue.length > 14)
          ) {
            setHelperText((item) => ({
              ...item,
              mobile: t('please-enter-valid-mobile-number'),
            }));
            setFilterValues((item) => ({
              ...item,
              mobile: newValue,
            }));
          } else {
            setHelperText((item) => ({ ...item, mobile: '' }));
            setFilterValues((item) => ({
              ...item,
              mobile: newValue.replaceAll(' ', ''),
            }));
          }
        }}
        onKeyUp={(e) => {
          let { value } = e.target;
          if (searchTimer.current) clearTimeout(searchTimer.current);
          searchTimer.current = setTimeout(() => {
            if (helperText.mobile !== '') return;
            setSelectedValuesInFilters((item) => ({
              ...item,
              'landline_number.phone': value
                .replaceAll(' ', '')
                .replaceAll('-', '')
                .replaceAll('(', '')
                .replaceAll(')', ''),
            }));
          }, 700);
        }}
        onKeyDown={() => {
          if (searchTimer) clearTimeout(searchTimer.current);
        }}
        parentTranslationPath={parentTranslationPath}
        translationPath={translationPath}
        fieldClasses='fields-content'
      />
      <AutocompleteComponent
        key={`vendorCategory`}
        selectedValues={selectedValuesInFilters?.vendorCategory}
        idRef={`vendorCategoryRef`}
        inputPlaceholder='select-vendor-category'
        multiple={false}
        inputClasses='fields-content'
        data={allItems && allItems.vendorCategory}
        wrapperClasses='w-25'
        displayLabel={(option) => (option && option.lookupItemName) || ''}
        onChange={(e, newValue) => {
          setSelectedValuesInFilters((item) => ({
            ...item,
            vendorCategory: newValue,
          }));
        }}
        isLoading={isLoading.vendorCategory}
        withLoader
        withoutSearchButton
        translationPath={translationPath}
        parentTranslationPath={parentTranslationPath}
        onOpen={() => {
          if (allItems?.vendorCategory?.length === 0)
            getLookupItemsByName('VendorCategory', 'vendorCategory');
        }}
      />
      <AutocompleteComponent
        key={`AgreementStatus`}
        selectedValues={selectedValuesInFilters.agreementStatus || null}
        idRef={`AgreementStatusRef`}
        inputPlaceholder='select-agreement-status'
        multiple={false}
        data={allItems.agreementStatus}
        wrapperClasses='w-25'
        inputClasses='fields-content'
        displayLabel={(option) => (option && option.lookupItemName) || ''}
        onChange={(e, newValue) => {
          setSelectedValuesInFilters((item) => ({
            ...item,
            agreementStatus: newValue || null,
          }));
        }}
        withoutSearchButton
        translationPath={translationPath}
        parentTranslationPath={parentTranslationPath}
        onOpen={() => {
          if (allItems?.agreementStatus?.length === 0)
            getLookupItemsByName('VendorAgreementStatus', 'agreementStatus');
        }}
      />
      <AutocompleteComponent
        key={`country`}
        selectedValues={filterValues?.country}
        idRef={`vcountryRef`}
        inputPlaceholder='select-country'
        multiple={false}
        data={allItems && allItems.country}
        wrapperClasses='w-25'
        inputClasses='fields-content'
        displayLabel={(option) => (option && option.lookupItemName) || ''}
        onChange={(e, newValue) => {
          setSelectedValuesInFilters((item) => ({
            ...item,
            'country.lookupItemName': newValue,
          }));
          setFilterValues((item) => ({
            ...item,
            country: newValue,
          }));
        }}
        isLoading={isLoading.country}
        withLoader
        withoutSearchButton
        translationPath={translationPath}
        parentTranslationPath={parentTranslationPath}
        onOpen={() => {
          if (allItems?.country?.length === 0)
            lookupItemsGetIdAPI('country', 'country', 16);
        }}
      />
      <AutocompleteComponent
        key={`city`}
        selectedValues={filterValues?.city}
        idRef={`cityRef`}
        inputPlaceholder='select-city'
        multiple={false}
        data={allItems && allItems.city}
        wrapperClasses='w-25'
        inputClasses='fields-content'
        displayLabel={(option) => (option && option.lookupItemName) || ''}
        onChange={(e, newValue) => {
          setSelectedValuesInFilters((item) => ({
            ...item,
            'city.lookupItemName': newValue,
          }));
          setFilterValues((item) => ({
            ...item,
            city: newValue,
          }));
        }}
        isLoading={isLoading.city}
        withLoader
        withoutSearchButton
        translationPath={translationPath}
        parentTranslationPath={parentTranslationPath}
        onOpen={() => {
          if (allItems?.city?.length === 0)
            lookupItemsGetIdAPI('city', 'city', 17);
        }}
      />
      <SelectComponet
        getIsChecked={getIsContactClassChecked}
        singleIndeterminate={
          vendorContactClasses &&
          vendorContactClasses.length &&
          vendorContactClasses.length > 0 &&
          allItems?.vendorClass.length > 0 &&
          allItems.vendorClass.length > vendorContactClasses.length
        }
        renderValue={(value) =>
          (vendorContactClasses.length > 0 && (
            <span>
              {value.map((option, mapIndex) => {
                let toReturn = '';
                const optionIndex = allItems?.vendorClass.findIndex(
                  (element) => element?.lookupItemName === option
                );
                if (optionIndex !== -1) {
                  toReturn +=
                    allItems.vendorClass &&
                    allItems.vendorClass[optionIndex]?.lookupItemName;
                  if (mapIndex < value.length - 1) toReturn += ', ';
                }
                return toReturn;
              })}
            </span>
          )) || <span>{t(`${translationPath}select-vendor-class`)}</span>
        }
        singleChecked={
          vendorContactClasses &&
          vendorContactClasses.length > 0 &&
          vendorContactClasses.length - 1
        }
        data={
          allItems &&
          allItems?.vendorClass.map((i) => ({ value: i.lookupItemName }))
        }
        value={vendorContactClassesFilter}
        isWithCheckAll
        valueInput='value'
        multiple={true}
        keyLoopBy='value'
        textInput='value'
        onSelectChanged={changeContactClass}
        themeClass='theme-default'
        idRef='changeContactClassRef'
        parentTranslationPath={parentTranslationPath}
        translationPath={translationPath}
        translationPathForData={translationPath}
        wrapperClasses='w-25 fields-content'
      />
      <AutocompleteComponent
        idRef='CompanyNameRef'
        inputPlaceholder='select company name'
        multiple={false}
        data={allItems.companyNames}
        selectedValues={filterValues.companyName}
        wrapperClasses='w-25'
        inputClasses='fields-content'
        displayLabel={(option) => option}
        onChange={(_, newValue) => {
          setSelectedValuesInFilters((item) => ({
            ...item,
            CompanyName: newValue,
          }));
          setFilterValues((item) => ({
            ...item,
            companyName: newValue,
          }));
        }}
        isLoading={isLoading.companyNames}
        withLoader
        withoutSearchButton
        onOpen={() => {
          if (allItems?.companyNames?.length === 0) getCompanyNames();
        }}
        onInputChange={(_e, value) => getCompanyNames(value)}
      />
    </div>
  );
};
